'use client';

/**
 * ChargeApplePay Component
 *
 * Headless processor for Apple Pay payments using native ApplePaySession API.
 * Does NOT render any UI - parent uses ChargeStatusDisplay for display.
 *
 * The ApplePaySession is always created externally (via nativeButton.onClick
 * in the gateway registry) and passed in as `clickContext`. This ensures
 * the session is created synchronously within a user gesture handler,
 * satisfying the browser's security requirement.
 *
 * Flow:
 * 1. Receive pre-created ApplePaySession via clickContext prop
 * 2. Attach event handlers (onvalidatemerchant, onpaymentauthorized, oncancel)
 * 3. Call session.begin() to show the Apple Pay sheet
 * 4. On authorization: update payment source with token, create purchase, poll for result
 */

import React, { useEffect, useRef } from 'react';
import { usePurchaseLog } from '../../config/PurchaseFlowConfigProvider';
import { usePurchaseAnalytics } from '../../analytics';
import { getClientPlatformForAPI, browserContext } from '../../utils/platform';
import {
  validateApplePayMerchant,
  updateApplePayToken,
  getProcessingData,
  createPurchase,
  getPurchaseStatus,
  ApiError
} from '../../api';
import { poll } from '../../utils/polling';
import type { ChargeProcessorProps } from '../../types';

export const ChargeApplePay: React.FC<ChargeProcessorProps> = ({
  amount,
  withBonus = false,
  paymentSourceId,
  purchaseType,
  advisorId,
  clickSource,
  purchaseContext,
  fxRate,
  currency,
  riskModel,
  riskModelConfig,
  clickContext,
  onStatusChange,
  onSuccess,
  onError,
  onCancel,
  startSignal = 0
}) => {
  const appleSessionRef = useRef<any>(null);
  const completedRef = useRef(false);
  const log = usePurchaseLog();
  const analytics = usePurchaseAnalytics();

  useEffect(() => {
    if (!startSignal) return undefined;
    completedRef.current = false;

    if (!clickContext) {
      log('error', 'Apple Pay', 'No clickContext (ApplePaySession) provided');
      onError({ success: false });
      return undefined;
    }

    if (completedRef.current) return undefined;

    appleSessionRef.current = clickContext;

    appleSessionRef.current.onvalidatemerchant = async (event: any) => {
      try {
        onStatusChange?.('authenticating', 'Validating with Apple...');
        log('info', 'Apple Pay', 'onvalidatemerchant', { validationURL: event.validationURL });

        const merchantSession = await validateApplePayMerchant(paymentSourceId, event.validationURL, fxRate, currency);

        if (appleSessionRef.current) {
          appleSessionRef.current.completeMerchantValidation(merchantSession);
        }
      } catch (error) {
        log('error', 'Apple Pay', 'Merchant validation failed', { error });
        try {
          appleSessionRef.current?.abort();
        } catch {
          // Session may already be inactive (dismissed/timed out)
        }
        onError({ success: false, errorMessage: error instanceof ApiError ? error.userMessage : undefined });
      }
    };

    appleSessionRef.current.onpaymentauthorized = async (event: any) => {
      try {
        const { payment } = event || {};
        if (!payment?.token) {
          if (appleSessionRef.current) {
            appleSessionRef.current.completePayment(window.ApplePaySession.STATUS_FAILURE);
          }
          onError({ success: false });
          return;
        }

        onStatusChange?.('processing', 'Processing authorization...');
        log('info', 'Apple Pay', 'onpaymentauthorized');

        appleSessionRef.current.completePayment(window.ApplePaySession.STATUS_SUCCESS);

        const { token, billingContact, shippingContact } = payment;

        onStatusChange?.('processing', 'Saving payment details...');
        await updateApplePayToken(paymentSourceId, token, billingContact, shippingContact);

        onStatusChange?.('processing', 'Creating purchase...');

        const processingData = await getProcessingData(amount, {
          use_reference_transaction: true,
          with3ds: true,
          with_bonus: withBonus,
          purchase_type: purchaseType?.toLowerCase(),
          context: purchaseContext,
          platform: getClientPlatformForAPI(),
          ...browserContext,
          ...(advisorId && { intended_advisor_id: advisorId }),
          ...(clickSource && { click_source: clickSource }),
          ...(currency && { currency }),
          ...(fxRate && { fx_rate: fxRate })
        });

        analytics.setConversionEventId(processingData.conversion_event_id);
        analytics.trackPurchaseStarted();

        const purchaseResponse = await createPurchase({
          requiredParams: processingData.required_params || {},
          riskModel,
          riskModelConfig,
          fxRate,
          currency
        });

        if (purchaseResponse.error) {
          log('error', 'Apple Pay', 'Create purchase error', {
            errorMessage: purchaseResponse.error,
            errorType: purchaseResponse.errorType
          });
          onError({
            success: false,
            errorMessage: purchaseResponse.error,
            errorType: purchaseResponse.errorType,
            errorTitle: purchaseResponse.errorTitle,
            recoverable: purchaseResponse.recoverable,
            retriable: purchaseResponse.retriable,
            errorOrigin: purchaseResponse.errorOrigin,
            errorPathname: purchaseResponse.errorPathname
          });
          return;
        }

        if (!purchaseResponse.id) {
          onError({ success: false });
          return;
        }

        onStatusChange?.('polling', 'Completing payment...');

        const response = await poll({
          pollFunction: () => getPurchaseStatus(
            purchaseResponse.id,
            getClientPlatformForAPI()
          ),
          checkComplete: (res) => !!(res.okMessage || res.errorMessage)
        });

        analytics.setExtraEventParams(response.extraEventParams);

        if (response.errorMessage) {
          log('error', 'Apple Pay', 'Apple Pay charge processing error (poll)', {
            errorMessage: response.errorMessage,
            errorType: response.errorType
          });
          onError({
            success: false,
            errorMessage: response.errorMessage,
            errorType: response.errorType,
            errorTitle: response.errorTitle,
            recoverable: response.recoverable,
            retriable: response.retriable,
            errorOrigin: response.errorOrigin,
            errorPathname: response.errorPathname
          });
          return;
        }

        appleSessionRef.current = null;
        onSuccess({
          success: true,
          userCreditId: purchaseResponse.id,
          okMessage: response.okMessage,
          cashbackAmount: response.cashbackAmount,
          pathname: response.pathname,
          threeDAuthResult: response.threeDAuthResult
        });
      } catch (error) {
        log('error', 'Apple Pay', 'Payment processing failed', { error });
        onError({ success: false, errorMessage: error instanceof ApiError ? error.userMessage : undefined });
      }
    };

    appleSessionRef.current.oncancel = () => {
      log('info', 'Apple Pay', 'User cancelled');
      onCancel?.();
    };

    onStatusChange?.('processing', 'Please complete payment in Apple Pay...');
    appleSessionRef.current.begin();

    return () => { completedRef.current = true; };
  }, [startSignal, clickContext, log]);

  return null;
};
